import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Check, 
  Clock,
  AlertTriangle,
  Settings,
  Loader2,
  BellRing
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Alerts = () => {
  const { language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const { permission, supported, isGranted, requestPermission, sendPolicyAlert } = usePushNotifications();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      const [alertsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/alerts/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAlerts(alertsRes.data);
      setPreferences(prefsRes.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar alertas' : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
      toast.success(language === 'es' ? 'Marcado como leído' : 'Marked as read');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed to update');
    }
  };

  const updatePreferences = async (newPrefs) => {
    setSavingPrefs(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.put(`${API}/alerts/preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(newPrefs);
      toast.success(language === 'es' ? 'Preferencias guardadas' : 'Preferences saved');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    } finally {
      setSavingPrefs(false);
    }
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === 'high') return <Badge className="risk-high">{language === 'es' ? 'Urgente' : 'Urgent'}</Badge>;
    if (urgency === 'medium') return <Badge className="risk-medium">{language === 'es' ? 'Medio' : 'Medium'}</Badge>;
    return <Badge className="risk-low">{language === 'es' ? 'Bajo' : 'Low'}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="alerts-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-12 w-full" />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {language === 'es' ? 'Centro de Alertas' : 'Alert Center'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'es' 
            ? 'Gestiona tus notificaciones y preferencias de alerta'
            : 'Manage your notifications and alert preferences'}
        </p>
      </div>

      <Tabs defaultValue="alerts" className="space-y-6">
        <TabsList>
          <TabsTrigger value="alerts" data-testid="alerts-tab">
            <Bell className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Alertas' : 'Alerts'}
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="preferences-tab">
            <Settings className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Preferencias' : 'Preferences'}
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {alerts.length > 0 ? (
            alerts.map((alert, index) => (
              <Card 
                key={alert.id}
                className={`transition-all ${!alert.read ? 'border-primary/50 bg-primary/5' : ''}`}
                data-testid={`alert-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 p-2 rounded-full ${
                        alert.urgency === 'high' ? 'bg-accent/10' : 
                        alert.urgency === 'medium' ? 'bg-warning/10' : 'bg-success/10'
                      }`}>
                        {alert.urgency === 'high' ? (
                          <AlertTriangle className="h-4 w-4 text-accent" />
                        ) : (
                          <Bell className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{alert.title}</h3>
                          {getUrgencyBadge(alert.urgency)}
                          {!alert.read && (
                            <Badge variant="secondary" className="text-xs">
                              {language === 'es' ? 'Nuevo' : 'New'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{alert.message}</p>
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {!alert.read && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => markAsRead(alert.id)}
                        data-testid={`mark-read-${index}`}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-medium text-lg mb-2">
                  {language === 'es' ? 'No hay alertas' : 'No alerts'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Te notificaremos cuando haya cambios de política relevantes'
                    : 'We\'ll notify you when there are relevant policy changes'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          {preferences && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Notification Channels */}
              <Card data-testid="notification-channels">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-primary" />
                    {language === 'es' ? 'Canales de Notificación' : 'Notification Channels'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Push Notifications */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BellRing className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <Label>{language === 'es' ? 'Notificaciones Push' : 'Push Notifications'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'es' ? 'Alertas instantáneas en el navegador' : 'Instant browser alerts'}
                        </p>
                      </div>
                    </div>
                    {supported ? (
                      isGranted ? (
                        <Badge className="bg-success/20 text-success">
                          {language === 'es' ? 'Activo' : 'Active'}
                        </Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            const result = await requestPermission();
                            if (result === 'granted') {
                              toast.success(language === 'es' ? '¡Notificaciones activadas!' : 'Notifications enabled!');
                            } else if (result === 'denied') {
                              toast.error(language === 'es' ? 'Permiso denegado' : 'Permission denied');
                            }
                          }}
                          data-testid="enable-push-btn"
                        >
                          {language === 'es' ? 'Activar' : 'Enable'}
                        </Button>
                      )
                    ) : (
                      <Badge variant="secondary">
                        {language === 'es' ? 'No soportado' : 'Not supported'}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <Label>{language === 'es' ? 'Correo Electrónico' : 'Email'}</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'es' ? 'Recibir alertas por correo' : 'Receive alerts via email'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.email_enabled}
                      onCheckedChange={(checked) => 
                        updatePreferences({ ...preferences, email_enabled: checked })
                      }
                      data-testid="email-toggle"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <Label>SMS</Label>
                        <p className="text-sm text-muted-foreground">
                          {language === 'es' ? 'Recibir alertas por SMS' : 'Receive alerts via SMS'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={preferences.sms_enabled}
                      onCheckedChange={(checked) => 
                        updatePreferences({ ...preferences, sms_enabled: checked })
                      }
                      data-testid="sms-toggle"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Alert Categories */}
              <Card data-testid="alert-categories">
                <CardHeader>
                  <CardTitle>
                    {language === 'es' ? 'Categorías de Alerta' : 'Alert Categories'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['environmental', 'voting', 'housing'].map((cat) => (
                    <div key={cat} className="flex items-center justify-between">
                      <Label className="capitalize">
                        {cat === 'environmental' ? (language === 'es' ? 'Ambiental' : 'Environmental') :
                         cat === 'voting' ? (language === 'es' ? 'Votación' : 'Voting') :
                         (language === 'es' ? 'Vivienda' : 'Housing')}
                      </Label>
                      <Switch
                        checked={preferences.categories.includes(cat)}
                        onCheckedChange={(checked) => {
                          const newCategories = checked
                            ? [...preferences.categories, cat]
                            : preferences.categories.filter(c => c !== cat);
                          updatePreferences({ ...preferences, categories: newCategories });
                        }}
                        data-testid={`category-${cat}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Jurisdictions */}
              <Card data-testid="jurisdictions">
                <CardHeader>
                  <CardTitle>
                    {language === 'es' ? 'Jurisdicciones' : 'Jurisdictions'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['federal', 'state', 'local'].map((jur) => (
                    <div key={jur} className="flex items-center justify-between">
                      <Label className="capitalize">
                        {jur === 'federal' ? (language === 'es' ? 'Federal' : 'Federal') :
                         jur === 'state' ? (language === 'es' ? 'Estatal' : 'State') :
                         (language === 'es' ? 'Local' : 'Local')}
                      </Label>
                      <Switch
                        checked={preferences.jurisdictions.includes(jur)}
                        onCheckedChange={(checked) => {
                          const newJurs = checked
                            ? [...preferences.jurisdictions, jur]
                            : preferences.jurisdictions.filter(j => j !== jur);
                          updatePreferences({ ...preferences, jurisdictions: newJurs });
                        }}
                        data-testid={`jurisdiction-${jur}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Urgency Threshold */}
              <Card data-testid="urgency-settings">
                <CardHeader>
                  <CardTitle>
                    {language === 'es' ? 'Umbral de Urgencia' : 'Urgency Threshold'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {language === 'es' 
                      ? 'Recibir notificaciones solo para alertas de este nivel o superior'
                      : 'Only receive notifications for alerts at this level or higher'}
                  </p>
                  <div className="flex gap-2">
                    {['low', 'medium', 'high'].map((level) => (
                      <Button
                        key={level}
                        variant={preferences.urgency_threshold === level ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updatePreferences({ ...preferences, urgency_threshold: level })}
                        data-testid={`urgency-${level}`}
                      >
                        {level === 'low' ? (language === 'es' ? 'Bajo' : 'Low') :
                         level === 'medium' ? (language === 'es' ? 'Medio' : 'Medium') :
                         (language === 'es' ? 'Alto' : 'High')}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Test Push Notification */}
              {supported && isGranted && (
                <Card data-testid="test-notification">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BellRing className="h-5 w-5 text-primary" />
                      {language === 'es' ? 'Probar Notificaciones' : 'Test Notifications'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      {language === 'es' 
                        ? 'Envía una notificación de prueba para verificar que funcionan correctamente'
                        : 'Send a test notification to verify they work correctly'}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        sendPolicyAlert({
                          id: 'test',
                          title: language === 'es' ? 'Prueba de Alerta de Política' : 'Test Policy Alert',
                          description: language === 'es' 
                            ? 'Esta es una notificación de prueba de PoliBot. ¡Las notificaciones push están funcionando correctamente!'
                            : 'This is a test notification from PoliBot. Push notifications are working correctly!',
                          risk_score: 75,
                          plain_language_summary: 'This is a test notification from PoliBot. Push notifications are working correctly!',
                          plain_language_summary_es: 'Esta es una notificación de prueba de PoliBot. ¡Las notificaciones push están funcionando correctamente!'
                        }, language);
                        toast.success(language === 'es' ? '¡Notificación de prueba enviada!' : 'Test notification sent!');
                      }}
                      data-testid="test-notification-btn"
                    >
                      <BellRing className="mr-2 h-4 w-4" />
                      {language === 'es' ? 'Enviar Prueba' : 'Send Test'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Alerts;
